import { useNavigate, useParams } from 'react-router';
import { useForm } from 'react-hook-form';
import { useEffect, useMemo } from 'react';
import { styled } from '@mui/material';

import {
  Typography,
  Box,
  Form,
  Select,
  TextField,
  Button,
} from '@app-velocity/ui';

import {
  ScenarioRoutePaths,
  useGetScenarioQuery,
  useUpdateScenarioMutation,
} from '@app-velocity/modules/scenarios';
import { useI18n } from '@app-velocity/i18n';
import { ClassValidatorResolver } from '@app-velocity/validators';
import { MessageBus } from '@app-velocity/message-bus';
import { FrameIcon } from '@app-velocity/icons';

import { PrimaryPage } from '../../../../layouts';
import { ConfigureHumanTestingDto } from '../../../../dto/ConfigureHumanTestingDto';

const SetUpActions = styled(Box)({
  paddingTop: '12px',
  display: 'flex',
  flexWrap: 'nowrap',
  gap: '12px',
  justifyContent: 'flex-end',
  alignItems: 'flex-end',
  flex: 1,
});

const StyledTypography = styled(Typography)({
  color: 'var(--input-placeholder-color)',
  fontSize: 13,
  opacity: 0.4,
});

const messageBus = new MessageBus('prototypes');

export const SetUpHumanTesting = () => {
  const { t } = useI18n();
  const {
    handleSubmit,
    control,
    register,
    formState: { errors: formErrors },
  } = useForm<ConfigureHumanTestingDto>({
    mode: 'onSubmit',
    resolver: ClassValidatorResolver(ConfigureHumanTestingDto),
  });
  const navigate = useNavigate();

  const { teamId, workspaceId, scenarioId } = useParams<{
    teamId: string;
    workspaceId: string;
    scenarioId: string;
  }>();

  const { data: scenario } = useGetScenarioQuery(workspaceId, scenarioId);

  const {
    mutate: updateScenario,
    data: updatedScenario,
    isPending: isUpdating,
    isSuccess,
  } = useUpdateScenarioMutation(workspaceId, scenarioId);

  const onSubmit = async (data: ConfigureHumanTestingDto) => {
    updateScenario(data);
  };

  const goBack = () => {
    navigate(
      ScenarioRoutePaths.challengeDetails(teamId!, workspaceId!, scenarioId!)
    );
  };

  useEffect(() => {
    if (!isSuccess || !updatedScenario?.shareToken || !updatedScenario.id)
      return;

    messageBus.publish('modal:share-scenario:open', {
      shareToken: updatedScenario.shareToken,
      prototypeId: updatedScenario.id,
    });
    goBack();
  }, [isSuccess, updatedScenario]);

  const sortedScreens = useMemo(() => {
    if (!scenario || !scenario.prototypePages) return [];

    return [...scenario.prototypePages].sort(
      (a, b) => (a.index || 0) - (b.index || 0)
    );
  }, [scenario]);

  return (
    <PrimaryPage
      back={ScenarioRoutePaths.challengeDetails(
        teamId!,
        workspaceId!,
        scenarioId!
      )}
      backLabel={scenario?.name || ''}
      title={t('prototypes:set_up_human_testing')}
    >
      <Form onSubmit={handleSubmit(onSubmit)}>
        <Form.FieldContainer>
          <Form.Label>{t('prototypes:final_screen')}</Form.Label>
          <Select
            variant="filled"
            {...register('finalTriggerElement')}
            displayEmpty
            fullWidth
            size="medium"
            error={!!formErrors.finalTriggerElement}
            renderValue={(selected: unknown) => {
              if (!selected)
                return (
                  <StyledTypography>
                    {t('prototypes:final_screen_placeholder')}
                  </StyledTypography>
                );

              return (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <FrameIcon />
                  {sortedScreens.find((screen) => screen.id === selected)?.name}
                </Box>
              );
            }}
          >
            {sortedScreens.map((page) => (
              <Select.Item
                key={page.id!}
                value={page.id!}
                data-ph-capture-attribute-id="challenge_final_screen_option"
              >
                <FrameIcon />
                {page.name}
              </Select.Item>
            ))}
          </Select>
        </Form.FieldContainer>
        <Form.FieldContainer>
          <Form.Label>{t('prototypes:final_question')}</Form.Label>
          <TextField
            name="finalQuestion"
            control={control}
            fullWidth
            inputProps={{
              'data-ph-capture-attribute-id': 'challenge_final_question',
            }}
            multiline
            error={!!formErrors.finalQuestion}
            helperText={formErrors.finalQuestion?.message}
            placeholder={t('prototypes:final_question_placeholder')}
          />
        </Form.FieldContainer>
        <SetUpActions>
          <Button variant="contained" color="secondary" onClick={goBack}>
            {t('shared:cancel')}
          </Button>
          <Button variant="contained" disabled={isUpdating} type="submit">
            {t('shared:create')}
          </Button>
        </SetUpActions>
      </Form>
    </PrimaryPage>
  );
};
